import { useEffect, useRef, useState } from 'react'
import { Play, Pause, Loader2, FileText } from 'lucide-react'
import { chatService } from '../../services/chatService'
import { toast } from '../../stores/uiStore'

/**
 * Sesli mesaj oynatıcısı (web).
 *
 * Ek indirmesi Authorization başlığı ister → `<audio src="/api/...">` ÇALIŞMAZ.
 * Bayt blob olarak çekilip object URL'e bağlanır; bileşen kalkarken URL revoke
 * edilir (yoksa uzun sohbet oturumunda her dinlenen ses bellekte kalır).
 */
// Tarayıcı bu biçimi çözemiyorsa (ör. AAC desteği olmayan Chromium) bunu söyle —
// "Ses açılamadı" tek başına 403 / ağ / codec hatasını ayırt ettirmiyordu.
function _playErr(e) {
  const n = (e && e.name) || ''
  if (n === 'NotSupportedError') return 'Tarayıcı bu ses biçimini çalamıyor'
  if (n === 'NotAllowedError') return 'Oynatma engellendi — tekrar dokun'
  return 'Ses açılamadı'
}

export default function VoicePlayer({ attachment: a, company }) {
  const [url, setUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [pos, setPos] = useState(0)
  const [dur, setDur] = useState((a.duration_ms || 0) / 1000)
  const [speed, setSpeed] = useState(1)
  const [error, setError] = useState(null)
  // Transkript: kayıtta varsa doğrudan gösterilir, yoksa istek üzerine üretilir
  const [transcript, setTranscript] = useState(a.transcript || null)
  const [txLoading, setTxLoading] = useState(false)
  const audioRef = useRef(null)

  const bars = (a.waveform && a.waveform.length
    ? a.waveform.split('').map((c) => Number(c) || 2)
    : Array(28).fill(3))

  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])

  const load = async () => {
    setLoading(true); setError(null)
    try {
      let blob = await chatService.fetchAttachmentBlob(company, a.id)
      // Sunucudan gelen MIME ses değilse (octet-stream — mobil yüklemeleri böyle
      // gelebiliyordu) <audio> blob'u çözemez → uzantıdan tip ver.
      if (!(blob.type || '').startsWith('audio/')) {
        const ext = ((a.file_name || '').split('.').pop() || '').toLowerCase()
        const byExt = { m4a: 'audio/mp4', aac: 'audio/aac', mp3: 'audio/mpeg', ogg: 'audio/ogg',
          opus: 'audio/ogg', wav: 'audio/wav', webm: 'audio/webm' }[ext]
        if (byExt) blob = new Blob([blob], { type: byExt })
      }
      const u = URL.createObjectURL(blob)
      setUrl(u)
      return u
    } catch {
      setError('Ses açılamadı')
      return null
    } finally { setLoading(false) }
  }

  const toggle = async () => {
    const el = audioRef.current
    if (playing) { el?.pause(); return }
    if (!url) {
      const u = await load()
      if (!u) return
      // src atandıktan sonra oynat — effect değil, doğrudan (kullanıcı jesti içinde)
      setTimeout(() => { audioRef.current?.play().catch((e) => setError(_playErr(e))) }, 0)
      return
    }
    el?.play().catch((e) => setError(_playErr(e)))
  }

  const fmt = (s) => {
    if (!isFinite(s) || s < 0) s = 0
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`
  }
  const progress = dur > 0 ? Math.min(1, pos / dur) : 0

  const doTranscribe = async () => {
    if (transcript || txLoading) return
    setTxLoading(true)
    try {
      const r = await chatService.transcribeAttachment(company, a.id)
      const d = r?.data || {}
      if (d.transcript) setTranscript(d.transcript)
      // Kapalı/başarısızsa SESSİZ kalınmaz — kullanıcı neden metin gelmediğini bilmeli
      else toast.info('Metne çevrilemedi', d.message || 'Ses anlaşılamadı')
    } catch (e) {
      toast.error('Hata', e.response?.data?.detail || e.message)
    } finally { setTxLoading(false) }
  }

  return (
    <div className="w-[260px] max-w-full">
    <div className="flex items-center gap-2.5">
      <button type="button" onClick={toggle} disabled={loading}
        className="w-8 h-8 shrink-0 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center hover:bg-primary-100 disabled:opacity-60">
        {loading ? <Loader2 size={15} className="animate-spin" /> : (playing ? <Pause size={16} /> : <Play size={16} />)}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-[2px] h-6 cursor-pointer"
          onClick={(e) => {
            const el = audioRef.current
            if (!el || !dur) return
            const r = e.currentTarget.getBoundingClientRect()
            el.currentTime = ((e.clientX - r.left) / r.width) * dur
          }}>
          {bars.map((l, i) => (
            <div key={i} className={`flex-1 rounded-sm ${(i / bars.length) <= progress ? 'bg-primary-500' : 'bg-gray-300'}`}
              style={{ height: `${3 + l * 2.1}px` }} />
          ))}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[10.5px] ${error ? 'text-red-500' : 'text-gray-400'}`}>
            {error || (pos > 0 ? `${fmt(pos)} / ${fmt(dur)}` : fmt(dur))}
          </span>
          {url && (
            <button type="button"
              onClick={() => {
                const next = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1
                setSpeed(next)
                if (audioRef.current) audioRef.current.playbackRate = next
              }}
              className="ml-auto text-[10px] font-bold text-primary-600 bg-gray-100 rounded px-1.5 py-0.5 hover:bg-gray-200">
              {speed === 1 ? '1x' : speed === 1.5 ? '1.5x' : '2x'}
            </button>
          )}
        </div>
      </div>
      <button type="button" onClick={doTranscribe} disabled={txLoading || !!transcript}
        title={transcript ? 'Metin aşağıda' : 'Metne çevir'}
        className="p-1.5 shrink-0 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-100 disabled:opacity-40">
        {txLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
      </button>
      {url && (
        <audio ref={audioRef} src={url} preload="metadata"
          onLoadedMetadata={(e) => { if (isFinite(e.target.duration)) setDur(e.target.duration) }}
          onTimeUpdate={(e) => setPos(e.target.currentTime)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={(e) => { setPlaying(false); e.target.currentTime = 0; setPos(0) }} />
      )}
    </div>
    {transcript && (
      <div className="mt-1.5 text-[12px] text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 whitespace-pre-wrap">
        {transcript}
      </div>
    )}
    </div>
  )
}
